/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import AccordionWrapper from '../../accordion/Accordion';
import {AccordionSectionHeader} from '../../accordion/current/AccordionSectionHeader';
import {ASSET_DETAILS_CARD_WIDTH, getAccordionTopBorderStyle} from './assetDetailsAccordionStyles';

export interface AssetDetailsSectionAccordionProps {
  title: string;
  children: ReactNode;
  // replaces the section's title header - e.g. a breadcrumb back to the
  // section's own top level ...
  header?: ReactNode;
  trailingContent?: ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

const useStyles = makeStyles(() => ({
  // every card in the Asset Details Drawer is a fixed 456px (ION-7461) ...
  wrapper : {
    boxSizing : 'border-box',
    width     : ASSET_DETAILS_CARD_WIDTH
  },
  header : getAccordionTopBorderStyle(),
  body   : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%',
    gap           : '16px'
  }
}));

/**
 * Renders a current theme Asset Details Drawer section accordion - the same
 * fixed-width card, 2px teal top border and uppercase
 * `AccordionSectionHeader` title as the drawer's Additional Info,
 * Diagnostics and Tags accordions - around any section content, for the
 * sections without a design of their own (Smarter Trailer, Generic Sensor
 * and the SC device sensor graphs).
 *
 * Expansion is uncontrolled via `defaultExpanded` by default; passing
 * `expanded` and `onExpand` instead hands control to the caller, which the
 * Asset Details Drawer uses to drive its header's expand/collapse-all
 * control.
 *
 * ```typescript
 *
 *   import {AssetDetailsSectionAccordion} from
 *     '@ion/dls/src/components/assetdetails/current/AssetDetailsSectionAccordion';
 *   ...
 *
 *   <AssetDetailsSectionAccordion title='Generic Sensor'
 *     expanded={expanded}
 *     onExpand={onExpand}>
 *     <GenericSensorLineChart data={data} chartData={chartData} />
 *   </AssetDetailsSectionAccordion>
 *
 * ```
 *
 * @param   title           The section's title.
 * @param   children        The section's content.
 * @param   header          Replaces the title header, if given.
 * @param   trailingContent Shown right after the title, if given.
 * @param   defaultExpanded Starts expanded when `true` (uncontrolled).
 * @param   expanded        The controlled expanded state.
 * @param   onExpand        Invoked with the new expanded state.
 * @returns                 The section accordion.
 */
export const AssetDetailsSectionAccordion = ({
  title,
  children,
  header,
  trailingContent,
  defaultExpanded,
  expanded,
  onExpand
}: AssetDetailsSectionAccordionProps) => {
  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <AccordionWrapper isCustomStyle
        className={classes.header}
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onExpand={onExpand}
        header={header ?? <AccordionSectionHeader title={title} trailingContent={trailingContent} />}
        details={
          <div className={classes.body}>
            {children}
          </div>
        } />
    </div>
  );
};

export default React.memo(AssetDetailsSectionAccordion);
